import { useState } from 'react';

import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { SelfMatchAction } from 'components/types';

import {
  EmployeeCostCenterType,
  SelfMatchAction as SelfMatchActionEnum
} from 'components/enums';

import { openForSelfMatch } from 'RestAPIs/roster/requests/self-match';

import { Box, Typography } from '@mui/material';
import Button from '@mui/material/Button';
import DialogActions from '@mui/material/DialogActions';

import { Dialog } from 'components/shared';
import CheckboxTable from 'components/shared/self-match/CheckboxTable';
import MatchActionSelector from 'components/shared/self-match/MatchActionSelector';

const OpenSelfMatchDialogStyles = styled(Box)`
  table {
    > tbody td:first-child {
      width: 120px;
    }
  }
`;

type Props = {
  open?: true;
  requestIds: number[];
  hasConnect?: true;
  hasSubscribe?: true;
  onHidden: () => void;
  promiseCallback: (promise: Promise<any>) => void;
};

export default function OpenSelfMatchDialog({
  open = true,
  onHidden,
  requestIds,
  hasSubscribe,
  hasConnect,
  promiseCallback
}: Props) {
  const { t } = useTranslation('common');
  const [hide, setHide] = useState(false);
  const defaultAction = hasConnect
    ? SelfMatchActionEnum.CONNECT
    : hasSubscribe
    ? SelfMatchActionEnum.SUBSCRIBE
    : SelfMatchActionEnum.NONE;
  const [own, setOwn] = useState<SelfMatchAction>(defaultAction);
  const [shared, setShared] = useState<SelfMatchAction>(defaultAction);
  const [preference1, setPreference1] =
    useState<SelfMatchAction>(defaultAction);
  const [preference2, setPreference2] = useState<SelfMatchAction>(
    SelfMatchActionEnum.NONE
  );
  const matchActions = [
    SelfMatchActionEnum.NONE,
    hasSubscribe ? SelfMatchActionEnum.SUBSCRIBE : null,
    hasConnect ? SelfMatchActionEnum.CONNECT : null
  ].flatMap((a) => (a ? [a] : []));
  const actions = [own, shared, preference1, preference2];
  const allActionsNone = actions.every((a) => a === SelfMatchActionEnum.NONE);
  const allActionsSubscribe = hasSubscribe
    ? actions.every((a) => a === SelfMatchActionEnum.SUBSCRIBE)
    : null;
  const allActionsConnect = hasConnect
    ? actions.every((a) => a === SelfMatchActionEnum.CONNECT)
    : null;

  const handleClose = () => {
    setHide(true);
  };

  const sendRequests = () => {
    const data = [
      { employeeCostCenterType: EmployeeCostCenterType.OWN, action: own },
      { employeeCostCenterType: EmployeeCostCenterType.SHARED, action: shared },
      {
        employeeCostCenterType: EmployeeCostCenterType.PREFERENCE1,
        action: preference1
      },
      {
        employeeCostCenterType: EmployeeCostCenterType.PREFERENCE2,
        action: preference2
      }
    ];
    promiseCallback(openForSelfMatch(requestIds, data));
    handleClose();
  };

  return (
    <Dialog
      title={t('openForSelfMatch')}
      handleClose={handleClose}
      TransitionProps={{ onExited: onHidden }}
      open={hide ? false : open}
      maxWidth="xs"
    >
      <OpenSelfMatchDialogStyles>
        <Box display="flex" alignItems="end" mb={1}>
          <Typography variant="body2" sx={{ width: '120px' }}>{`${t(
            'openSelfMatchDialog.title'
          )}:`}</Typography>
          <CheckboxTable
            checks={[allActionsNone, allActionsSubscribe, allActionsConnect]}
            onChecked={(action) => {
              setOwn(action);
              setShared(action);
              setPreference1(action);
              setPreference2(action);
            }}
          />
        </Box>
        <table>
          <tbody>
            <tr>
              <td>{`${t('own')}:`}</td>
              <MatchActionSelector
                value={own}
                matchActions={matchActions}
                onClick={setOwn}
              />
            </tr>
            <tr>
              <td>{`${t('shared')}:`}</td>
              <MatchActionSelector
                value={shared}
                matchActions={matchActions}
                onClick={setShared}
              />
            </tr>
            <tr>
              <td>{`${t('preference1')}:`}</td>
              <MatchActionSelector
                value={preference1}
                matchActions={matchActions}
                onClick={setPreference1}
              />
            </tr>
            <tr>
              <td>{`${t('preference2')}:`}</td>
              <MatchActionSelector
                value={preference2}
                matchActions={matchActions}
                onClick={setPreference2}
              />
            </tr>
          </tbody>
        </table>
      </OpenSelfMatchDialogStyles>
      <DialogActions>
        <Button onClick={sendRequests} variant="contained">
          {t('send')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
